"use client"

import { useEffect, useState } from "react"

import { useTranslations } from "next-intl"

import { CouponTicket } from "@/components/marketplace"
import { Button, ShareIcon } from "@/components/ui"
import { useProductGallery } from "@/features/referrals/hooks/use-product-gallery"
import { useReferralTermsCopy } from "@/features/referrals/hooks/use-referral-terms-copy"
import { ReferralShareDialog } from "@/features/referrals/ReferralShareDialog"
import { useReferralOffer, type ReferralOfferState } from "@/lib/hooks/data/use-referrals"
import { useIsWebView } from "@/lib/providers/WebViewProvider"
import { markOrderReferralSeen, orderReferralSeen } from "@/lib/referrals/attribution"
import { isSuppressedInAppMode } from "@/platform/app-mode/suppressions"

const AUTO_OPEN_DELAY_MS = 900

type ReadyOffer = Extract<ReferralOfferState, { status: "ready" }>

interface ReferralTicketProps {
  imageUrls: string[]
  productTitle?: string
  order?: { cartId: string; productId: string | null }
}

const TicketBody = ({ offer, imageUrls, productTitle, order, open, setOpen }: ReferralTicketProps & { offer: ReadyOffer; open: boolean; setOpen: (open: boolean) => void }) => {
  const t = useTranslations("referrals")
  const copy = useReferralTermsCopy(offer.me.terms)
  const productLabel = () => (copy.earns ? t("product.ticket", { amount: copy.maxEarning }) : t("product.ticketFriendOnly", { discount: copy.discount }))
  const label = order ? t(copy.earns ? "order.ticket" : "order.ticketFriendOnly", { discount: copy.discount }) : productLabel()
  return (
    <>
      <CouponTicket
        art="/images/referrals/discount-bubble.svg"
        action={
          <Button size="sm" fullWidth iconRight={<ShareIcon size={16} />} onClick={() => setOpen(true)} data-testid={order ? "order-referral-share" : "pdp-recommend"}>
            {t("share")}
          </Button>
        }
      >
        {label}
      </CouponTicket>
      <ReferralShareDialog offer={offer} open={open} onClose={() => setOpen(false)} imageUrls={imageUrls} productTitle={productTitle} />
    </>
  )
}

export const ReferralTicket = ({ imageUrls, productTitle, order }: ReferralTicketProps) => {
  const isWebView = useIsWebView()
  const offer = useReferralOffer()
  const [open, setOpen] = useState(false)
  const visible = offer.status === "ready" && !(order && isSuppressedInAppMode("post-checkout-referral", isWebView))
  const gallery = useProductGallery(order?.productId ?? null, visible && order !== undefined)
  const imagesSettled = !order || order.productId === null || gallery.isFetched
  const cartId = order?.cartId ?? null

  useEffect(() => {
    if (!visible || cartId === null || !imagesSettled || orderReferralSeen(cartId)) return
    const timer = setTimeout(() => {
      markOrderReferralSeen(cartId)
      setOpen(true)
    }, AUTO_OPEN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [visible, imagesSettled, cartId])

  if (!visible || offer.status !== "ready") return null

  return <TicketBody offer={offer} imageUrls={gallery.data?.length ? gallery.data : imageUrls} productTitle={productTitle} order={order} open={open} setOpen={setOpen} />
}
